import { terminalPortStorageKey, terminalStorageKey } from '@/constants/self-checkin-device-storage'
import { onlyForLocal } from '@/utils/check-environment'
import * as api from './aipass'

export const DeviceType = {
  PaidCreditCard: 'PCC',
  PaidQr: 'PQR',
} as const
export type DeviceType = (typeof DeviceType)[keyof typeof DeviceType]

export type CreditSalesSlip = {
  type: 'credit'
  transactionAt: string
  slipNumber: string
  approvalCode: string
  accountNumber: string
}

export type QRSalesSlip = {
  type: 'qr'
  transactionAt: string
  slipNumber: string
  transactionNumber: string
}

export type OutputCompleteEvent = {
  CurrentService: string
  SequenceNumber: number
  SettledAmount: number
  AdditionalSecurityInformation: {
    brand: string
  }
  pmsResult: {
    paymentMethod: string
    orderId: string
    amount: number
    totalAmountAt10Per: number
    totalAmountAt8Per: number
    totalTaxAt10Per: number
    totalTaxAt8Per: number
    salesSlip: CreditSalesSlip | QRSalesSlip | undefined
  }
}
export type ErrorEvent = {
  ErrorCode: number
  ErrorCodeExtended: number
  SequenceNumber: number
  CurrentService: string
  Errorcodedetail: string
  Message?: string
}

type StatusEvent = {
  ResponseCode: string
}

type AuthorizeSalesCommand = {
  AuthorizeSales: {
    SequenceNumber: number
    CurrentService: 'UnionPay' | 'Credit'
    Amount: number
    TaxOthers: number
    TrainingMode: boolean
    AdditionalSecurityInformation: {
      lang: 'ja' | 'en' | 'zh' | 'ko'
      apStatusOption: 0 | 1
    }
  }
}
type SubtractValueCommand = {
  SubtractValue: {
    SequenceNumber: number
    CurrentService: 'QRPayment'
    Amount: number
    TrainingMode: boolean
    AdditionalSecurityInformation: {
      qrCodeMode: 'CPM'
      qrCode?: string
    }
  }
}

type VescaHandler = {
  onSuccess: (data: OutputCompleteEvent) => void
  onCancel: (data: ErrorEvent) => void
  onError: (data?: ErrorEvent) => void
  onReconnect?: () => void
  onProcess?: () => void
}

const hasEvent = (data: unknown, eventKey: 'ErrorEvent' | 'OutputCompleteEvent'): boolean => {
  if (!data || typeof data !== 'object') {
    return false
  }
  return Object.prototype.hasOwnProperty.call(data, eventKey)
}

const isErrorEventData = (data: unknown): data is { ErrorEvent: ErrorEvent } => {
  return hasEvent(data, 'ErrorEvent')
}

const isOutputCompleteEventData = (data: unknown): data is { OutputCompleteEvent: OutputCompleteEvent } => {
  return hasEvent(data, 'OutputCompleteEvent')
}

const isNetworkError = (event: ErrorEvent): boolean => {
  return event.ErrorCode === 990 && event.Errorcodedetail === '0129' && event.ErrorCodeExtended === -1
}

const isNeedPrintRetry = (event: ErrorEvent): boolean => {
  return event.ErrorCode === 900 && event.Errorcodedetail === '0140' && event.ErrorCodeExtended === -1
}

const isCancelFromPos = (event: ErrorEvent): boolean => {
  return event.ErrorCode === 114 && event.ErrorCodeExtended === -1
}

const isCancelFromTerminal = (event: ErrorEvent): boolean => {
  return event.ErrorCode === 114 && event.ErrorCodeExtended === -2
}

/**
 * @see https://docs.google.com/spreadsheets/d/1BqAVP-ZZo1RbIZF0PG3fpwI--YjbrUL-
 */
export class VescaJs {
  public inProgress: boolean = false
  private currentSequenceNumber: number | undefined = undefined
  private suppressMainWorkerErrorOnErrorEvent = false
  private handler: VescaHandler
  private hotelId: string
  private paymentTarget: {
    reservationIds?: string[] | undefined
    guestRoomAssignIds?: string[] | undefined
    salesIds?: string[] | undefined
  }
  private FullFeaturedWorker: Worker | undefined = undefined
  private config: { host: string; port: number }
  private unloadHandler?: () => void
  private disposeRequested = false

  private paymentCompletionPollingTimer: ReturnType<typeof setInterval> | undefined = undefined
  private paymentCompletionPollingWorker: Worker | undefined = undefined
  private paymentCompletionPollingRestartTimer: ReturnType<typeof setTimeout> | undefined = undefined

  constructor(params: {
    hotelId: string
    handler: VescaHandler
    paymentTarget: {
      reservationIds?: string[]
      guestRoomAssignIds?: string[]
      salesIds?: string[]
    }
    config?: { host: string; port: number }
  }) {
    this.hotelId = params.hotelId
    this.handler = params.handler
    this.paymentTarget = params.paymentTarget
    this.config = params.config || {
      host: localStorage.getItem(terminalStorageKey) || '',
      port: Number(localStorage.getItem(terminalPortStorageKey)) || 3647,
    }
    try {
      this.FullFeaturedWorker = this.createWorker(params.handler)
    } catch (error) {
      this.storeVescaResponse('Error', { message: `Failed to create polling worker: ${error}` }).catch(() => {})
      params.handler.onError()
    }
  }

  private makeTerminalHost(): string {
    const schema = window.location.protocol === 'https:' ? 'wss' : 'ws'
    return `${schema}://${this.config.host}:${this.config.port}`
  }

  private async storeVescaResponse(command: 'OutputCompleteEvent' | 'ErrorEvent' | string, event: OutputCompleteEvent | ErrorEvent | any) {
    const sequenceNumber = event.SequenceNumber || this.currentSequenceNumber
    const paymentTarget =
      sequenceNumber !== this.currentSequenceNumber
        ? {
            reservationIds: undefined,
            guestRoomAssignIds: undefined,
            salesIds: undefined,
          }
        : this.paymentTarget
    return await api.storeVescaHistory({
      hotelId: this.hotelId,
      host: this.makeTerminalHost(),
      sequenceNumber,
      direction: 'response',
      command,
      amount: event.SettledAmount,
      message: event,
      checkinId: undefined,
      ...paymentTarget,
    })
  }

  private storeVescaRequest(
    command: 'SubtractValue' | 'AuthorizeSales' | 'Cancel' | 'StartService' | 'PrintRetry' | string,
    event: AuthorizeSalesCommand | SubtractValueCommand | any,
  ) {
    api.storeVescaHistory({
      hotelId: this.hotelId,
      host: this.makeTerminalHost(),
      sequenceNumber: event[command].SequenceNumber || this.currentSequenceNumber,
      direction: 'request',
      command,
      amount: event[command].Amount,
      message: event,
      checkinId: undefined,
      reservationIds: this.paymentTarget.reservationIds,
    })
  }

  private schedulePaymentCompletionPollingRestart(handler: VescaHandler) {
    if (this.disposeRequested) {
      return
    }
    if (this.paymentCompletionPollingRestartTimer !== undefined) {
      return
    }
    this.paymentCompletionPollingRestartTimer = setTimeout(() => {
      this.paymentCompletionPollingRestartTimer = undefined
      if (this.disposeRequested || this.paymentCompletionPollingTimer !== undefined) {
        return
      }
      this.startPaymentCompletionPolling(handler)
    }, 3000)
  }

  private startPaymentCompletionPolling(handler: VescaHandler) {
    if (this.paymentCompletionPollingTimer !== undefined) return

    try {
      this.paymentCompletionPollingWorker = new Worker('/vescajs-fullfeaturedws.min.js')
    } catch (error) {
      this.storeVescaResponse('PollingError', { message: `Failed to create polling worker: ${error}` }).catch(() => {})
      this.schedulePaymentCompletionPollingRestart(handler)
      return
    }
    this.paymentCompletionPollingWorker.onmessage = async e => {
      if (e.data !== 'undefined' && e.data.StatusEvent != null) {
        return
      }
      if (isOutputCompleteEventData(e.data)) {
        const event = e.data.OutputCompleteEvent as OutputCompleteEvent
        if (!event.SequenceNumber) {
          // 端末が待機状態になっているため操作のやり直し
          this.storeVescaResponse('PollingOutputCompleteEvent', event).catch(() => {})
          this.notifyProcessDone(() => handler.onError())
          return
        }
        this.handleOutputComplete(handler, event)
        return
      }
      if (!isErrorEventData(e.data)) {
        return
      }
      const event = e.data.ErrorEvent as ErrorEvent
      this.storeVescaResponse('PollingErrorEvent', event).catch(() => {})
      if (!isNeedPrintRetry(event)) {
        return
      }
      this.postPrintRetry(this.paymentCompletionPollingWorker)
    }

    this.paymentCompletionPollingWorker.onerror = () => {
      this.storeVescaResponse('PollingError', { message: 'There is an error with polling worker!"' }).catch(() => {})
      this.stopPaymentCompletionPolling()
      this.schedulePaymentCompletionPollingRestart(handler)
    }
    this.paymentCompletionPollingWorker.onmessageerror = e => {
      this.storeVescaResponse('PollingMessageError', { message: `Error receiving message from polling worker: ${e}` }).catch(() => {})
      this.stopPaymentCompletionPolling()
      this.schedulePaymentCompletionPollingRestart(handler)
    }

    this.paymentCompletionPollingTimer = setInterval(() => {
      if (!this.paymentCompletionPollingWorker) {
        return
      }
      this.postStartService(this.paymentCompletionPollingWorker)
    }, 3000)

    this.postStartService(this.paymentCompletionPollingWorker)
  }

  private stopPaymentCompletionPolling() {
    if (this.paymentCompletionPollingTimer !== undefined) {
      clearInterval(this.paymentCompletionPollingTimer)
      this.paymentCompletionPollingTimer = undefined
    }

    if (this.paymentCompletionPollingRestartTimer !== undefined) {
      clearTimeout(this.paymentCompletionPollingRestartTimer)
      this.paymentCompletionPollingRestartTimer = undefined
    }

    if (this.paymentCompletionPollingWorker) {
      this.paymentCompletionPollingWorker.terminate()
      this.paymentCompletionPollingWorker = undefined
    }
  }

  private notifyProcessDone(cb: () => void) {
    this.stopPaymentCompletionPolling()
    cb()
  }

  private handleOutputComplete(handler: VescaHandler, event: OutputCompleteEvent) {
    this.stopPaymentCompletionPolling()
    this.storeVescaResponse('OutputCompleteEvent', event).then(res => {
      const isSameSequence = this.currentSequenceNumber === event.SequenceNumber
      if (!isSameSequence) {
        handler.onError()
        return
      }

      if (res?.result) {
        handler.onSuccess({ ...event, pmsResult: res.result })
        return
      }
      handler.onSuccess(event)
    })
  }

  private createWorker(handler: VescaHandler) {
    const worker = new Worker('/vescajs-fullfeaturedws.min.js')
    let hasReceivedStatusEvent = false
    worker.onmessage = async e => {
      if (e.data !== 'undefined') {
        if (e.data.StatusEvent != null) {
          hasReceivedStatusEvent = true
          const event = e.data.StatusEvent as StatusEvent
          this.storeVescaResponse('StatusEvent', event).catch(() => {})
          this.startPaymentCompletionPolling(handler)
          if (event.ResponseCode[2] === 'N') {
            handler.onProcess?.()
          }
          return
        }
      }

      if (isErrorEventData(e.data)) {
        this.inProgress = false
        const event = e.data.ErrorEvent as ErrorEvent
        this.storeVescaResponse('ErrorEvent', event).catch(() => {})
        if (isCancelFromTerminal(event)) {
          this.notifyProcessDone(() => handler.onCancel(event))
        } else if (isCancelFromPos(event)) {
          // POS（=PMS）キャンセルはdoCancel呼び出し側でコントロールするため通知なし
        } else if (isNeedPrintRetry(event)) {
          this.startPaymentCompletionPolling(handler)
          this.postPrintRetry(worker)
        } else {
          if (hasReceivedStatusEvent) {
            handler.onReconnect?.()
            return
          }
          if (this.suppressMainWorkerErrorOnErrorEvent && isNetworkError(event)) {
            // qrは端末通信中でもネットワークエラーが返却される
            // ネットワーク断か接続エラーか判別できないため、polling側で待機中となるまで待機させる
            return
          }
          this.notifyProcessDone(() => handler.onError(event))
        }
      } else if (isOutputCompleteEventData(e.data)) {
        this.inProgress = false
        const event = e.data.OutputCompleteEvent as OutputCompleteEvent
        this.handleOutputComplete(handler, event)
      } else {
        this.storeVescaResponse('Unknown', e.data).catch(() => {})
        this.notifyProcessDone(() => handler.onError())
      }
    }
    worker.onerror = e => {
      this.storeVescaResponse('Error', { message: 'There is an error with your worker!"' }).catch(() => {})
    }
    worker.onmessageerror = e => {
      this.storeVescaResponse('MessageError', { message: `Error receiving message from worker: ${e}` }).catch(() => {})
    }
    return worker
  }

  private bindUnloadAutoCancel() {
    if (this.unloadHandler) {
      return
    }

    this.unloadHandler = () => {
      this.dispose()
    }
    window.addEventListener('beforeunload', this.unloadHandler)
    window.addEventListener('pagehide', this.unloadHandler)
  }

  private doCancel() {
    if (!this.inProgress || !this.FullFeaturedWorker) {
      return
    }
    const request = { Cancel: true }
    this.storeVescaRequest('Cancel', request)
    this.FullFeaturedWorker.postMessage({ request })
  }

  private postStartService(worker: Worker, isPolling: boolean = false) {
    const request = { StartService: '' }
    this.storeVescaRequest(`${isPolling ? 'Polling' : ''}StartService`, request)
    worker.postMessage({ host: this.makeTerminalHost(), request })
  }

  /**
   * @param {Object} request
   */
  private postPayRequest(request: AuthorizeSalesCommand | SubtractValueCommand) {
    if (!this.FullFeaturedWorker) {
      return
    }
    const command = Object.keys(request).includes('SubtractValue') ? 'SubtractValue' : 'AuthorizeSales'
    this.storeVescaRequest(command, request)
    this.bindUnloadAutoCancel()
    this.inProgress = true
    this.currentSequenceNumber = request[command].SequenceNumber
    this.FullFeaturedWorker.postMessage({
      host: this.makeTerminalHost(),
      request,
    })
  }

  private postPrintRetry(worker: Worker | undefined): void {
    if (!worker) {
      return
    }
    const request = {
      PrintRetry: {
        SequenceNumber: this.currentSequenceNumber || this.makeSequenceNumber(),
        CurrentService: 'All',
        TrainingMode: onlyForLocal,
      },
    }
    this.storeVescaRequest('PrintRetry', request)
    worker.postMessage({
      host: this.makeTerminalHost(),
      request,
    })
  }

  private makeSequenceNumber() {
    return Math.max(1, Math.floor(Math.random() * 2147483647))
  }

  /**
   * @see 5-1.QR決済（REQUEST）
   * @param params
   */
  doQrPay(params: { amount: number; tax: number; qrCode: string }) {
    this.suppressMainWorkerErrorOnErrorEvent = true
    this.postPayRequest({
      SubtractValue: {
        SequenceNumber: this.makeSequenceNumber(),
        CurrentService: 'QRPayment',
        Amount: params.amount,
        TrainingMode: onlyForLocal,
        AdditionalSecurityInformation: {
          qrCodeMode: 'CPM',
          qrCode: params.qrCode,
        },
      },
    })
    // doQrPayではStatusEvent未着のケースでも復旧できるよう、送信後3秒でpolling開始を予約する
    this.schedulePaymentCompletionPollingRestart(this.handler)
  }

  /**
   * @see 5-1.QR決済（REQUEST）端末側QRリーダ読み取り
   * @param params
   */
  doQrPayWithQRReader(params: { amount: number; tax: number }) {
    this.suppressMainWorkerErrorOnErrorEvent = false
    this.postPayRequest({
      SubtractValue: {
        SequenceNumber: this.makeSequenceNumber(),
        CurrentService: 'QRPayment',
        Amount: params.amount,
        TrainingMode: onlyForLocal,
        AdditionalSecurityInformation: { qrCodeMode: 'CPM' },
      },
    })
  }

  /**
   * @see 2-1.クレジット・銀聯（REQUEST）
   * @param params
   */
  doCreditPay(params: { amount: number; tax: number; lang?: 'ja' | 'en' | 'zh' | 'ko'; unionPay?: boolean }) {
    this.suppressMainWorkerErrorOnErrorEvent = false
    this.postPayRequest({
      AuthorizeSales: {
        SequenceNumber: this.makeSequenceNumber(),
        CurrentService: params.unionPay ? 'UnionPay' : 'Credit',
        Amount: params.amount,
        TaxOthers: params.unionPay ? 0 : params.tax,
        TrainingMode: onlyForLocal,
        AdditionalSecurityInformation: { lang: params.lang || 'en', apStatusOption: 1 },
      },
    })
  }

  doConnect() {
    if (!this.FullFeaturedWorker) {
      return
    }
    this.postStartService(this.FullFeaturedWorker)
  }

  dispose() {
    this.disposeRequested = true
    this.stopPaymentCompletionPolling()

    if (this.unloadHandler) {
      window.removeEventListener('beforeunload', this.unloadHandler)
      window.removeEventListener('pagehide', this.unloadHandler)
      this.unloadHandler = undefined
    }

    if (this.inProgress && this.FullFeaturedWorker) {
      this.doCancel()
      return
    }

    this.FullFeaturedWorker?.terminate()
    this.FullFeaturedWorker = undefined
  }
}
